import { FC, useEffect, useState } from "react";
import "./ModalSortArticleInsert.scss";
import ModalDefault from "../../../components/ModalDefault/ModalDefault";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../../providers/AuthProvider";
import { useForm } from "react-hook-form";
import { Card, Col, FormGroup, ListGroup, Row } from "react-bootstrap";
import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";
import { schema } from "./Validate";
import { useAgentsCategories } from "../../../providers/Agents/AgentsCategories/AgentsCategoriesProvider";
import ReactSelect from "react-select";
import { customStyles } from "../../../models/SelectCustomStyles";
import { useSections } from "../../../providers/Articles/Sections/SectionsProvider";
import { useSubsections } from "../../../providers/Articles/Subsections/SubsectionsProvider";
import { useArticles } from "../../../providers/Articles/ArticlesProvider";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { ArticlesModel } from "../../../models/Articles/ArticleModel";
import Skeleton from "react-loading-skeleton";
import { ReactSelectOptions } from "../../../models/ReactSelectOptions";

interface ModalSortArticleInsertProps {
  show: boolean;
  onClose: () => void;
}

const ModalSortArticleInsert: FC<ModalSortArticleInsertProps> = ({
  show,
  onClose,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] =
    useState<ReactSelectOptions | null>(null);
  const [selectedSection, setSelectedSection] =
    useState<ReactSelectOptions | null>(null);
  const [selectedSubsection, setSelectedSubsection] =
    useState<ReactSelectOptions | null>(null);
  const [sectionsReady, setSectionsReady] = useState<boolean>(false);
  const [subsectionsReady, setSubsectionsReady] = useState<boolean>(false);

  const [sortArticlesInfo, setSortArticlesInfo] = useState<ArticlesModel[]>([]);

  const { onInvalid } = useAuth();
  const { categoriesOptions, setParams: setParamsAgentsCategories } =
    useAgentsCategories();
  const {
    sectionsOptions,
    setParams: setParamsSections,
    setSectionsOptions,
    isLoading: isLoadingSections,
  } = useSections();

  const {
    subsectionsOptions,
    setParams: setParamsSubsections,
    setSubsectionsOptions,
    isLoading: isLoadingSubsections,
  } = useSubsections();

  const {
    sortArticles,
    setSortArticles,
    handleListSortArticle,
    handleSortArticle,
    isLoadingSortArticle,
  } = useArticles();

  const {
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const reset = () => {
    setSelectedCategories(null);
    setSelectedSection(null);
    setSelectedSubsection(null);
    setSectionsReady(false);
    setSubsectionsReady(false);
    setParamsAgentsCategories({
      withPaginate: false,
      filter: { only_active: true },
    });
    setParamsSections("");
    setParamsSubsections("");
    setSortArticles("");
    setSectionsOptions([]);
    setSubsectionsOptions([]);
    setSortArticlesInfo([]);
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleSelectedCategory = (option: ReactSelectOptions | null) => {
    setSelectedCategories(option);
    setSelectedSection(null);
    setSelectedSubsection(null);
    setSectionsReady(false);
    setSubsectionsReady(false);

    if (!option) {
      setSectionsOptions([]);
      setSubsectionsOptions([]);
      setSortArticlesInfo([]);
    }
  };

  const handleSelectedSection = (option: ReactSelectOptions | null) => {
    setSelectedSection(option);
    setSelectedSubsection(null);
    setSubsectionsReady(false);

  }
    useEffect(() => {
    if (selectedCategories && !isLoadingSortArticle) {
      setSectionsReady(true);
    } else {
      setSectionsReady(false);
    }
  }, [selectedCategories, isLoadingSortArticle]);

  useEffect(() => {
    if (selectedSection && !isLoadingSortArticle) {
      setSubsectionsReady(true);
    } else {
      setSubsectionsReady(false);
    }
  }, [selectedSection, isLoadingSortArticle]);

  useEffect(() => {
    if (sortArticles.length) {
      if (selectedCategories) {
        const item = sortArticles.filter(
            (x) => x?.section_id === null && x?.subsection_id === null,
        );
        if (item.length) {
          setSortArticlesInfo(item);
        } else {
          setSortArticlesInfo([]);
        }
      }

      if (selectedSection) {
          const item = sortArticles.filter((x) => x?.subsection_id === null);
          setSortArticlesInfo(item);
      }

      if (selectedSubsection) {
        setSortArticlesInfo(sortArticles);
      }
    } else {
      setSortArticlesInfo([]);
    }
  }, [sortArticles]);

  useEffect(() => {
    if (show) {
      setParamsAgentsCategories({
        withPaginate: false,
        filter: { only_active: true },
      });
    } else {
      reset();
    }
  }, [show]);

  useEffect(() => {
    setValue("category_id", selectedCategories?.value || null);
    if (selectedCategories) {
      setParamsSections({
        withPaginate: false,
        filter: { only_active: true, category_id: selectedCategories?.value },
      });
    }

    if (selectedSection) {
      setValue("section_id", selectedSection?.value || null);
      setParamsSubsections({
        withPaginate: false,
        filter: { only_active: true, section_id: selectedSection?.value },
      });
    }

    if (selectedSubsection) {
      setValue("subsection_id", selectedSubsection?.value || null);
    }
  }, [selectedCategories, selectedSection, selectedSubsection]);

  useEffect(() => {
    if (!selectedCategories && !selectedSection && !selectedSubsection) return;

    handleListSortArticle({
      withPaginate: false,
      filter: {
        only_active: true,
        category_id: selectedCategories?.value,
        section_id: selectedSection?.value,
        subsection_id: selectedSubsection?.value,
      },
    });
  }, [selectedCategories, selectedSection, selectedSubsection]);

  const handleOnDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(sortArticlesInfo);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSortArticlesInfo(items);
    await handleSortArticle({
      articles: items.map((item: any, index: number) => ({
        id: item.id,
        order: index,
      })),
    });
  };

  return (
    <ModalDefault
      title={"Ordenar Artigos"}
      show={show}
      onClose={handleClose}
      sizeModal={"xl"}
      showFooter={true}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div
        className="ModalSortArticleInsert"
        data-testid="ModalSortArticleInsert"
      >
        <Row>
          <Col md={5}>
            <Row>
              <Col md={12}>
                <FormGroup className="form-group">
                  <label htmlFor="category_id">CATEGORIA</label>
                  <ReactSelect
                    isSearchable
                    isClearable
                    options={categoriesOptions ?? []}
                    value={selectedCategories}
                    placeholder="Selecione"
                    className={`form-control p-0 ${onInvalid(
                      errors?.category_id,
                    )}`}
                    noOptionsMessage={() => "Não há registros"}
                    styles={customStyles}
                    onChange={(e) => {
                      handleSelectedCategory(e);
                    }}
                  />
                  <ErrorMessage
                    name={"Categoria"}
                    type={errors?.category_id?.type?.toString()}
                  />
                </FormGroup>
              </Col>
              
              <Col md={12}>
                <FormGroup className="form-group">
                  <label htmlFor="section_id">SEÇÃO</label>
                  <ReactSelect
                    isSearchable
                    isClearable
                    isDisabled={!selectedCategories || !sectionsReady}
                    options={sectionsOptions ?? []}
                    value={selectedSection}
                    placeholder={!sectionsReady && selectedCategories ? "Carregando..." : "Selecione"}
                    className={`form-control p-0 ${onInvalid(
                      errors?.section_id,
                    )}`}
                    noOptionsMessage={() => "Não há registros"}
                    styles={customStyles}
                    onChange={(e) => {
                      handleSelectedSection(e ?? null);
                    }}
                  />
                  <ErrorMessage
                    name={"Seção"}
                    type={errors?.section_id?.type?.toString()}
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup className="form-group">
                  <label htmlFor="subsection_id">SUBSEÇÃO</label>
                  <ReactSelect
                    isSearchable
                    isClearable
                    isDisabled={!selectedSection || !subsectionsReady}
                    options={subsectionsOptions ?? []}
                    value={selectedSubsection}
                    placeholder={!subsectionsReady && selectedSection ? "Carregando..." : "Selecione"}
                    className={`form-control p-0 ${onInvalid(
                      errors?.subsection_id,
                    )}`}
                    noOptionsMessage={() => "Não há registros"}
                    styles={customStyles}
                    onChange={(e) => {
                      setSelectedSubsection(e ?? null);
                    }}
                  />
                  <ErrorMessage
                    name={"Subseção"}
                    type={errors?.subsection_id?.type?.toString()}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Col>
          <Col md={7}>
            {!isLoadingSortArticle ? (
              <Card>
                <div className="p-4">
                  <h5 className="mb-4">Artigos</h5>

                  <ListGroup className="navbar-nav articles">
                    <DragDropContext onDragEnd={handleOnDragEnd}>
                      <Droppable droppableId="pages">
                        {(provided: any) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {sortArticlesInfo &&
                            sortArticlesInfo?.length > 0 ? (
                              <>
                                {sortArticlesInfo?.map(
                                  (item: ArticlesModel, key: number) => (
                                    <Draggable
                                      key={item.id}
                                      draggableId={
                                        item.id?.toString() ||
                                        key?.toString() ||
                                        ""
                                      }
                                      index={key}
                                    >
                                      {(provided: any, snapshot: any) => (
                                        <div className="ps-3 pe-1">
                                          <div
                                            className={`nav-link text-wrap d-flex align-items-center py-2 ${
                                              snapshot.isDragging
                                                ? "background"
                                                : ""
                                            }`}
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                          >
                                            <div
                                              className="text-center"
                                              {...provided.dragHandleProps}
                                            >
                                              <i className="fas fa-grip-vertical"></i>
                                            </div>
                                            <span className="nav-link-text ms-2">
                                              {item?.title || ""}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  ),
                                )}
                              </>
                            ) : (
                              <div className="d-flex justify-content-center">
                                Não há registro
                              </div>
                            )}

                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </ListGroup>
                </div>
              </Card>
            ) : (
              <Skeleton borderRadius={15} height={400} width="100%" />
            )}
          </Col>
        </Row>
      </div>
    </ModalDefault>
  );
};

export default ModalSortArticleInsert;
